import { supabase } from '../supabase/supabaseClient';
import { securityMonitoring } from '../security/securityLogging';

export interface ImpersonationData {
  isImpersonating: boolean;
  adminUserId?: string;
  adminEmail?: string;
  targetUserId?: string;
  targetEmail?: string;
  targetFirstName?: string;
  targetLastName?: string;
  startedAt?: string;
}

const IMPERSONATION_STORAGE_KEY = 'admin_impersonation_data';

/**
 * Start impersonating a user
 */
export async function startImpersonation(targetUserId: string): Promise<{ 
  success: boolean; 
  error?: string;
  impersonationUrl?: string;
}> {
  try {
    // Get current admin user
    const { data: { user: adminUser } } = await supabase.auth.getUser();
    
    if (!adminUser) {
      return { success: false, error: 'Not authenticated' };
    }

    // Call the edge function to start impersonation
    const { data, error } = await supabase.functions.invoke('admin-impersonate-user', {
      body: { targetUserId }
    });

    if (error) {
      console.error('Impersonation error:', error);
      console.error('Error details:', {
        message: error.message,
        status: error.status,
        statusText: error.statusText,
        context: error.context
      });
      
      // Try to extract more detailed error information from the context
      let detailedError = error.message || 'Unknown error';
      
      if (error.context && error.context instanceof Response) {
        try {
          const errorBody = await error.context.json();
          console.error('Error response body:', errorBody);
          detailedError = errorBody.error || errorBody.message || detailedError;
        } catch (e) {
          console.error('Could not parse error response:', e);
        }
      }
      
      return {
        success: false,
        error: `Failed to start impersonation: ${detailedError}. Please ensure the edge function is deployed and configured correctly.`
      };
    }

    if (!data || !data.success) {
      console.error('Impersonation failed:', data);
      return { success: false, error: data?.error || 'Failed to start impersonation' };
    }

    // Store impersonation data in sessionStorage
    const impersonationData: ImpersonationData = {
      isImpersonating: true,
      adminUserId: data.adminUser.id,
      adminEmail: data.adminUser.email,
      targetUserId: data.targetUser.id,
      targetEmail: data.targetUser.email,
      targetFirstName: data.targetUser.firstName,
      targetLastName: data.targetUser.lastName,
      startedAt: new Date().toISOString()
    };

    sessionStorage.setItem(IMPERSONATION_STORAGE_KEY, JSON.stringify(impersonationData));

    // Log the impersonation start
    await securityMonitoring.logEvent(
      'admin_impersonation_initiated',
      {
        adminUserId: adminUser.id,
        adminEmail: adminUser.email,
        targetUserId,
        targetEmail: data.targetUser.email
      },
      'info'
    );

    return { 
      success: true, 
      impersonationUrl: data.impersonationUrl 
    };
  } catch (error) {
    console.error('Exception in startImpersonation:', error);
    return { 
      success: false, 
      error: error instanceof Error ? error.message : 'Unknown error' 
    };
  }
}

/**
 * Stop impersonating and return to admin account
 */
export async function stopImpersonation(): Promise<{ success: boolean; error?: string }> {
  try {
    const impersonationData = getImpersonationData();
    
    if (!impersonationData.isImpersonating) {
      return { success: false, error: 'Not currently impersonating' };
    }

    // Log the impersonation end
    await securityMonitoring.logEvent(
      'admin_impersonation_ended',
      {
        adminUserId: impersonationData.adminUserId,
        adminEmail: impersonationData.adminEmail,
        targetUserId: impersonationData.targetUserId,
        targetEmail: impersonationData.targetEmail,
        duration: impersonationData.startedAt 
          ? Date.now() - new Date(impersonationData.startedAt).getTime()
          : 0
      },
      'info'
    );

    // Clear impersonation data
    sessionStorage.removeItem(IMPERSONATION_STORAGE_KEY);

    // Sign out the impersonated user
    await supabase.auth.signOut();

    return { success: true };
  } catch (error) {
    console.error('Exception in stopImpersonation:', error);
    return { 
      success: false, 
      error: error instanceof Error ? error.message : 'Unknown error' 
    };
  }
}

/**
 * Get current impersonation data
 */
export function getImpersonationData(): ImpersonationData {
  try {
    const stored = sessionStorage.getItem(IMPERSONATION_STORAGE_KEY);
    if (!stored) {
      return { isImpersonating: false };
    }

    const data = JSON.parse(stored) as ImpersonationData;
    return data;
  } catch (error) {
    console.error('Error reading impersonation data:', error);
    return { isImpersonating: false };
  }
}

/**
 * Check if currently impersonating
 */
export function isImpersonating(): boolean {
  return getImpersonationData().isImpersonating;
}

/**
 * Clear impersonation data (for cleanup)
 */
export function clearImpersonationData(): void {
  sessionStorage.removeItem(IMPERSONATION_STORAGE_KEY);
}